// The affordability line (Asana 1218957945501621, Affordability Line block):
// prices the user's setup budget against the cards visible in Recent Activity.
// Pure. Always names a real number, never overpromises: the count is how many
// of the visible cards fit cheapest-first, and a null-priced card is excluded.

export interface AffordableItem {
  id: string;
  name: string;
  pointValue: number;
  imageUrl: string;
}

export interface AffordabilityInput {
  /** The Recent Activity cards on screen, in display order; null prices already dropped */
  items: AffordableItem[];
  /** Unclaimed points plus points left on unfinished tasks (1,600 on day one) */
  budget: number;
  /** Points on tasks that are COMPLETED but not yet claimed */
  unclaimed: number;
  /** Points on tasks not yet completed */
  remaining: number;
  /** medianCardPointsThisWeek from the stats; 0 when the week has no trades */
  median: number;
}

export type AffordabilityBranch = 'allFit' | 'someFit' | 'oneFits' | 'noneFit' | 'partClaimed' | 'emptyFeed' | 'noMedian';

export interface AffordabilityResult {
  branch: AffordabilityBranch;
  /** Items sorted cheapest first */
  sorted: AffordableItem[];
  /** How many of `sorted` the budget reaches, cheapest first */
  affordableCount: number;
  count: number;
  total: number;
  /** Points short of taking every visible card */
  gap: number;
  /** The one card that fits (oneFits) */
  cardName: string | null;
  /** floor(budget / median) when the median is positive */
  typicalCount: number;
  budget: number;
  unclaimed: number;
  remaining: number;
  median: number;
}

export const computeAffordability = (input: AffordabilityInput): AffordabilityResult => {
  const { budget, unclaimed, remaining, median } = input;
  const sorted = [...input.items].filter((item) => item.pointValue > 0).sort((a, b) => a.pointValue - b.pointValue);
  const count = sorted.length;
  const total = sorted.reduce((sum, item) => sum + item.pointValue, 0);
  let spent = 0;
  let affordableCount = 0;
  sorted.forEach((item) => {
    if (spent + item.pointValue <= budget) {
      spent += item.pointValue;
      affordableCount += 1;
    }
  });
  const typicalCount = median > 0 ? Math.floor(budget / median) : 0;
  const base = {
    sorted,
    affordableCount,
    count,
    total,
    gap: Math.max(total - budget, 0),
    cardName: affordableCount === 1 ? sorted[0].name : null,
    typicalCount,
    budget,
    unclaimed,
    remaining,
    median,
  };

  if (count === 0) {
    return { branch: median > 0 ? 'emptyFeed' : 'noMedian', ...base };
  }
  // A reward sitting unclaimed changes the sentence, unless the claimable
  // points already take every card (review 2026-09-28): then it is "all fit"
  if (unclaimed > 0 && remaining > 0) {
    // Part-claimed prices the CLAIMABLE points against the cards
    let spentNow = 0;
    let fitNow = 0;
    sorted.forEach((item) => {
      if (spentNow + item.pointValue <= unclaimed) {
        spentNow += item.pointValue;
        fitNow += 1;
      }
    });
    if (fitNow < count) return { branch: 'partClaimed', ...base, affordableCount: fitNow };
  }
  if (affordableCount === 0) {
    return { branch: median > 0 ? 'noneFit' : 'noMedian', ...base };
  }
  if (affordableCount === 1) {
    return { branch: 'oneFits', ...base };
  }
  if (affordableCount === count) {
    return { branch: 'allFit', ...base };
  }
  return { branch: 'someFit', ...base };
};

export const plural = (n: number, one: string, many: string): string => (n === 1 ? one : many);
